"use client";

import React, { useState } from "react";
import Link from "next/link";
import { sanitizeHtml } from "../../utils/sanitizeHtml";

export interface CharitySummary {
  _id?: string;
  givingBlockId?: string;
  charityId?: string;
  name: string;
  description?: string;
  logo?: string;
  website?: string;
  wallet?: string;
  donationAddress?: string;
  tags?: string[];
  categories?: string[];
}

interface Props {
  charity: CharitySummary;
}

function truncateAddress(addr: string): string {
  if (addr.length <= 16) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
}

/**
 * Charity directory card.
 *
 * 2026-08-05 (client: "cards mismatch, some really too big, some went out of
 * size"). The card now has a fixed anatomy - header, flexible body, pinned
 * footer - so every card in a grid row ends up the same shape:
 *
 *   header   logo + name (name clamped to 2 lines)
 *   body     description + tags + links, `flex: 1` so it absorbs the extra
 *            height a stretched grid row hands the card
 *   footer   "Open charity profile", flush to the bottom at any height
 *
 * Previously the footer used a plain margin, so on a short card it sat
 * immediately after the content with dead space below it, and a row of four
 * cards had its call-to-action bars at four different heights.
 *
 * Overflow is handled in `.pr-charity-desc *` (styles/globals.css): charity
 * descriptions arrive as sanitized HTML and can contain images, tables, or
 * long unbroken URLs, and the previous inline `max-width: 100%` applied only
 * to the wrapper - which does not inherit - so wide children escaped the
 * card.
 *
 * Behaviour is unchanged: same link target, same expand toggle, same
 * clipboard copy.
 */
export default function CharityCard({ charity }: Props) {
  const id = charity.givingBlockId || charity.charityId || charity._id || charity.name;
  const href = `/charities/${encodeURIComponent(String(id))}`;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);

  const donationWallet = charity.wallet || charity.donationAddress || null;
  const initials = (charity.name || "?").slice(0, 3).toUpperCase();
  const showLogo = charity.logo && !imgError;

  function copyWallet(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!donationWallet) return;
    navigator.clipboard
      .writeText(donationWallet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => {});
  }

  return (
    <Link href={href} aria-label={`View charity: ${charity.name}`} className="pr-charity group">
      <span className="pr-charity-bloom" aria-hidden="true" />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <div className="pr-charity-logo">
          {showLogo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={charity.logo}
              alt={charity.name}
              className="h-full w-full object-contain p-1"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="text-xs font-bold tracking-wider text-emerald-400/90">{initials}</span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="pr-charity-name">{charity.name}</h3>
          {charity.categories && charity.categories.length > 0 && (
            <p className="mt-1 truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-400/70">
              {charity.categories.slice(0, 2).join(" · ")}
            </p>
          )}
        </div>
      </div>

      {/* ── Body (absorbs the row's extra height) ──────────────────────── */}
      <div className="pr-charity-body mt-3.5">
        {charity.description && (
          <div>
            <div
              className={`pr-charity-desc ${expanded ? "" : "pr-charity-desc-clamped"}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(charity.description) }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="mt-1.5 text-[10px] font-semibold text-emerald-400/75 transition-colors hover:text-emerald-300"
            >
              {expanded ? "Show less ↑" : "Read more ↓"}
            </button>
          </div>
        )}

        {charity.tags && charity.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {charity.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="pr-charity-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        {(charity.website || donationWallet) && (
          <div
            className="mt-3.5 flex flex-col gap-2 border-t pt-3"
            style={{ borderColor: "rgba(16,185,129,0.12)" }}
          >
            {charity.website && (
              <a
                href={charity.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-emerald-400 transition-colors hover:text-emerald-300"
                title={charity.website}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                {/* Flexible rather than a fixed 180px cap, which overflowed
                    once the card was narrower than that. */}
                <span className="min-w-0 flex-1 truncate">
                  {charity.website.replace(/^https?:\/\/(www\.)?/, "")}
                </span>
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 opacity-50"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}

            {donationWallet && (
              <button
                type="button"
                onClick={copyWallet}
                className="inline-flex w-full min-w-0 items-center gap-1.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-emerald-500/[0.12]"
                style={{
                  background: "rgba(16,185,129,0.06)",
                  border: "1px solid rgba(16,185,129,0.15)",
                }}
                title={`Copy wallet: ${donationWallet}`}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 text-emerald-400/70"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M16 12h.01" />
                </svg>
                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-emerald-300/70">
                  {truncateAddress(donationWallet)}
                </span>
                <span
                  className="shrink-0 font-sans text-[9px] font-bold uppercase tracking-[0.14em] transition-colors"
                  style={{ color: copied ? "#4ade80" : "rgba(110,231,183,0.5)" }}
                >
                  {copied ? "✓ Copied" : "Copy"}
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Footer (pinned) ────────────────────────────────────────────── */}
      <div className="pr-charity-foot">
        <span className="pr-charity-cta">
          <span>Open charity profile</span>
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </span>
      </div>
    </Link>
  );
}
